import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Pencil, Plus, Trash2, Zap } from "lucide-react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../ui/Collapsible";
import FieldLabel from "../ui/FieldLabel";
import DragNumberInput, { formatCompactNumberInputValue } from "../ui/DragNumberInput";
import Separator from "../ui/Separator";
import Tooltip from "../ui/Tooltip";
import { useSelectionStore } from "../../stores/selectionStore";
import { usePhysicsStore } from "../../stores/physicsStore";
import { useJoints, useBodies, useWorldBodies, useSceneStore } from "../../stores/sceneStore";
import { usePhysicsAuthoringLock } from "../../utils/physicsGuards";
import {
  createActuatorControlPayload,
  getActuatorInspectorSliderDisplayedValue,
  getActuatorPositionInspectorSliderLayout,
} from "../../physics/actuatorMapping";
import { getPhysicsWorker, hasPhysicsWorker } from "../../physics/workerHandle";
import { stopActiveServoSession } from "../../ik/ikTarget/servoSession";
import {
  INSPECTOR_CHROME_ICON_BUTTON_CLASS,
  INSPECTOR_CHROME_ICON_DELETE_CLASS,
  INSPECTOR_CHROME_SECTION_ICON_CLASS,
} from "../../utils/inspectorChromeButtonClasses";
import InfoTooltip from "../ui/InfoTooltip";
import MenuSelect from "../ui/MenuSelect";
import { resolveJointUuidFromRef } from "../../utils/jointUtils";
import { ACTUATOR_KIND_INFO_TEXT } from "../../utils/actuatorHelpText";

const ActuatorPhysicsPositionSlider = React.memo(function ActuatorPhysicsPositionSlider({
  actuator,
  jointOverride = null,
}) {
  const bodies = useBodies();
  const worldBodies = useWorldBodies();

  const isPhysicsOn = usePhysicsStore((s) => s.isRunning && hasPhysicsWorker());
  const worker = getPhysicsWorker();
  const setActuatorPositionTarget = usePhysicsStore((s) => s.actions.setActuatorPositionTarget);

  /** Output is stable across joint `ref`-only updates (whole `joints` map still gets a new reference). */
  const layout = useJoints(
    useCallback(
      (jm) =>
        getActuatorPositionInspectorSliderLayout(actuator, {
          joints: jm,
          bodies,
          worldBodies,
          actuatorJointOverride: jointOverride,
        }),
      [actuator, bodies, jointOverride, worldBodies]
    ),
    Object.is
  );

  const jointUuidForActuator = useJoints(
    useCallback(
      (jm) =>
        actuator?.joint && typeof actuator.joint === "string"
          ? resolveJointUuidFromRef(actuator.joint, jm)
          : null,
      [actuator?.joint]
    ),
    Object.is
  );

  const jointRefLive = useJoints(
    useCallback(
      (jm) => {
        if (!jointUuidForActuator) return 0;
        return Number(jm[jointUuidForActuator]?.ref) || 0;
      },
      [jointUuidForActuator]
    )
  );

  const sliderControlKey = layout?.controlKey ?? null;

  /** Only this actuator row re-subscribes to its physics target scalar; not every `ref`/`targets` churn. */
  const storedScalarTarget = usePhysicsStore(
    useCallback(
      (s) => (sliderControlKey != null ? s.actuatorPositionTargets?.[sliderControlKey] : undefined),
      [sliderControlKey]
    ),
    Object.is
  );

  const sliderState = useMemo(() => {
    if (!isPhysicsOn || !layout || sliderControlKey == null) return null;
    const sparseTargets =
      storedScalarTarget !== undefined ? { [sliderControlKey]: storedScalarTarget } : {};
    const value = getActuatorInspectorSliderDisplayedValue(layout, sparseTargets, jointRefLive);
    return {
      controlKey: sliderControlKey,
      min: layout.min,
      max: layout.max,
      step: layout.step,
      value,
      decimals: layout.decimals,
    };
  }, [isPhysicsOn, layout, sliderControlKey, storedScalarTarget, jointRefLive]);

  const handleSliderChange = useCallback(
    (event) => {
      if (!sliderState) return;
      const raw = parseFloat(event.target.value);
      if (Number.isNaN(raw)) return;
      const clamped =
        Number.isFinite(sliderState.min) &&
        Number.isFinite(sliderState.max) &&
        sliderState.max > sliderState.min
          ? Math.max(sliderState.min, Math.min(sliderState.max, raw))
          : raw;
      stopActiveServoSession();
      setActuatorPositionTarget(sliderState.controlKey, clamped);
      if (!worker) return;
      const actuatorListFull = Object.values(useSceneStore.getState().actuators || {});
      const payload = createActuatorControlPayload(
        { [sliderState.controlKey]: clamped },
        actuatorListFull
      );
      if (Object.keys(payload).length > 0) {
        worker.postMessage({ type: "controls", payload });
      }
    },
    [sliderState, worker, setActuatorPositionTarget]
  );

  if (!sliderState) return null;

  return (
    <div className="space-y-1 rounded-md border border-amber-600/25 bg-amber-500/5 px-2 py-2">
      <div className="flex items-center gap-1.5 text-xs text-gray-400">
        <Zap className="w-3 h-3 shrink-0 text-amber-500" aria-hidden />
        <span>Simulating — actuator target</span>
      </div>
      <div className="flex items-center gap-2 min-w-0">
        <input
          type="range"
          min={sliderState.min}
          max={sliderState.max}
          step={sliderState.step}
          value={sliderState.value}
          onChange={handleSliderChange}
          className="flex-1 min-w-0 h-2 slider"
          style={{ accentColor: "#f97316" }}
          aria-label={`Actuator position target for ${actuator?.name || "actuator"}`}
        />
        <span className="text-xs text-gray-300 shrink-0 tabular-nums w-[52px] text-right select-none">
          {sliderState.value.toFixed(sliderState.decimals)}
        </span>
      </div>
    </div>
  );
});

const ACTUATOR_KINDS = ["motor", "position", "velocity", "general"];

const ActuatorAccordion = ({
  actuators,
  sceneActions,
  addActuatorJoint = null,
  addActuatorTendon = null,
}) => {
  const [editingInputs, setEditingInputs] = useState({});
  const [editingNameUuid, setEditingNameUuid] = useState(null);
  const [nameDraft, setNameDraft] = useState("");
  const [scrollToUuid, setScrollToUuid] = useState(null);
  const [highlightUuid, setHighlightUuid] = useState(null);
  const rowRefs = useRef({});
  const expandedActuatorUuid = useSelectionStore((s) => s.expandedActuatorUuid);
  const setExpandedActuatorUuid = useSelectionStore((s) => s.setExpandedActuatorUuid);
  const disableAddActuator = usePhysicsAuthoringLock();

  const transmissionJointUuid = addActuatorJoint?.uuid ?? null;
  const transmissionTendonUuid = addActuatorTendon?.uuid ?? null;
  const canOfferAdd =
    !!(transmissionJointUuid || transmissionTendonUuid) &&
    typeof sceneActions?.addActuator === "function";

  useEffect(() => {
    if (!expandedActuatorUuid) return;
    const listForMatch = Array.isArray(actuators) ? actuators : [];
    const match = listForMatch.find((a) => a.uuid === expandedActuatorUuid);
    if (match) {
      setScrollToUuid(expandedActuatorUuid);
      setHighlightUuid(expandedActuatorUuid);
      setExpandedActuatorUuid(null);
      return;
    }
    setExpandedActuatorUuid(null);
  }, [expandedActuatorUuid, actuators, setExpandedActuatorUuid]);

  useEffect(() => {
    if (!scrollToUuid) return;
    const id = setTimeout(() => {
      rowRefs.current[scrollToUuid]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      setScrollToUuid(null);
    }, 80);
    return () => clearTimeout(id);
  }, [scrollToUuid]);

  useEffect(() => {
    if (!highlightUuid) return;
    const id = setTimeout(() => setHighlightUuid(null), 1400);
    return () => clearTimeout(id);
  }, [highlightUuid]);

  const handleAddActuator = useCallback(() => {
    if (disableAddActuator || typeof sceneActions?.addActuator !== "function") return;
    if (transmissionJointUuid) {
      const liveJoint = useSceneStore.getState().joints?.[transmissionJointUuid];
      const r = Array.isArray(liveJoint?.range) ? liveJoint.range : null;
      const defaultCtrlrange = r != null && r.length === 2 ? [Number(r[0]), Number(r[1])] : [-1, 1];
      const baseName = addActuatorJoint?.name ?? liveJoint?.name ?? "joint";
      sceneActions.addActuator({
        name: `${baseName}_actuator`,
        kind: "motor",
        joint: transmissionJointUuid,
        ctrlrange: defaultCtrlrange,
        gear: 1,
      });
      return;
    }
    if (transmissionTendonUuid) {
      const tName = addActuatorTendon?.name ?? transmissionTendonUuid;
      sceneActions.addActuator({
        name: `${tName}_actuator`,
        kind: "motor",
        tendonUuid: transmissionTendonUuid,
        tendon: tName,
        ctrlrange: [-1, 1],
        gear: 1,
      });
    }
  }, [
    disableAddActuator,
    sceneActions,
    transmissionJointUuid,
    transmissionTendonUuid,
    addActuatorJoint?.name,
    addActuatorTendon?.name,
  ]);

  const patchActuator = useCallback(
    (uuid, patch) => sceneActions.updateActuator(uuid, patch),
    [sceneActions]
  );

  const handleRemoveActuator = useCallback(
    (uuid) => {
      if (disableAddActuator || typeof sceneActions?.removeActuator !== "function" || !uuid) return;
      setEditingNameUuid((cur) => (cur === uuid ? null : cur));
      try {
        const sel = useSelectionStore.getState();
        if (sel.selectedActuator === uuid) sel.selectActuator(uuid);
        if (sel.expandedActuatorUuid === uuid) sel.setExpandedActuatorUuid(null);
      } catch {
        /* ignore */
      }
      sceneActions.removeActuator(uuid);
    },
    [disableAddActuator, sceneActions]
  );

  const beginNameEdit = useCallback((act) => {
    setEditingNameUuid(act.uuid);
    setNameDraft(String(act.name || "").trim() || "Actuator");
  }, []);

  const commitNameEdit = useCallback(
    (act) => {
      const trimmed = nameDraft.trim();
      setEditingNameUuid(null);
      if (!trimmed) return;
      const prev = String(act.name || "").trim();
      if (trimmed !== prev) {
        sceneActions.updateActuator(act.uuid, { name: trimmed });
      }
    },
    [nameDraft, sceneActions]
  );

  const cancelNameEdit = useCallback((act) => {
    setEditingNameUuid(null);
    setNameDraft(String(act.name || "").trim() || "Actuator");
  }, []);

  useEffect(() => {
    if (!editingNameUuid) return;
    const listCheck = Array.isArray(actuators) ? actuators : [];
    if (!listCheck.some((a) => a?.uuid === editingNameUuid)) {
      setEditingNameUuid(null);
    }
  }, [actuators, editingNameUuid]);

  const list = Array.isArray(actuators) ? actuators : [];
  const showEditControls = !disableAddActuator;
  const showSection = list.length > 0 || (canOfferAdd && showEditControls);
  const jointMapForActuatorControls = useJoints((jm) => jm);

  if (!showSection) return null;

  const dragInput = (actuatorUuid, key, label, value, onChange, opts = {}) => {
    const inputKey = `${actuatorUuid}:${key}`;
    const isEditing = editingInputs[inputKey] !== undefined;
    const displayValue = isEditing ? editingInputs[inputKey] : value;
    const inline = !!opts.inline;
    return (
      <DragNumberInput
        label={label}
        value={displayValue}
        dragValue={value}
        onDragValueChange={onChange}
        onChange={(e) => {
          setEditingInputs((prev) => ({ ...prev, [inputKey]: e.target.value }));
          onChange(parseFloat(e.target.value));
        }}
        onFocus={() => setEditingInputs((prev) => ({ ...prev, [inputKey]: String(value) }))}
        onBlur={(e) => {
          onChange(parseFloat(e.target.value));
          setEditingInputs((prev) => {
            const next = { ...prev };
            delete next[inputKey];
            return next;
          });
        }}
        wrapperClassName={
          inline ? "flex flex-row items-center gap-1.5 min-w-0 w-full" : "space-y-1 min-w-0"
        }
        labelClassName={
          inline
            ? "inline-flex items-center gap-1 text-xs panel-text-helper shrink-0 whitespace-nowrap"
            : "inline-flex items-center gap-1 text-xs panel-text-helper"
        }
        inputClassName={
          inline
            ? "min-w-0 w-full flex-1 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-100 text-sm"
            : "w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-100 text-sm"
        }
        inputWrapperClassName={inline ? "min-w-0 flex-1" : "min-w-0"}
        displayValueFormatter={opts.displayValueFormatter}
        inputProps={{ step: opts.step ?? "0.1", min: opts.min }}
        tooltipContent={opts.tooltip}
      />
    );
  };

  return (
    <>
      <Separator />
      <Collapsible defaultOpen>
        <CollapsibleTrigger
          quiet
          aria-label="Toggle Actuators"
          actions={
            canOfferAdd && showEditControls ? (
              <Tooltip content="Add actuator">
                <button
                  type="button"
                  aria-label="Add actuator"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAddActuator();
                  }}
                  className={INSPECTOR_CHROME_SECTION_ICON_CLASS}
                  style={{ opacity: 0.55 }}
                >
                  <Plus size={16} aria-hidden="true" />
                </button>
              </Tooltip>
            ) : null
          }
        >
          <span className="min-w-0 truncate text-left">Actuators</span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="pt-2 space-y-4 text-sm text-gray-300">
            {list.length === 0 && canOfferAdd ? (
              <div className="rounded border border-dashed border-gray-600/45 bg-gray-900/25 px-2 py-2">
                <button
                  type="button"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.preventDefault();
                    handleAddActuator();
                  }}
                  className="inline-flex w-full items-center justify-center gap-1 text-xs text-gray-200 transition-colors hover:text-gray-100"
                >
                  <span>Add new</span>
                  <Plus className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
                </button>
              </div>
            ) : null}
            {list.map((act, actIdx) => {
              const isPosition = act.kind === "position";
              const actuatorJointUuid =
                isPosition && act?.joint && typeof act.joint === "string"
                  ? resolveJointUuidFromRef(act.joint, jointMapForActuatorControls)
                  : null;
              const actuatorJoint = actuatorJointUuid
                ? jointMapForActuatorControls[actuatorJointUuid]
                : isPosition && transmissionJointUuid
                  ? jointMapForActuatorControls[transmissionJointUuid] || addActuatorJoint || null
                  : null;
              const ctrlMinRaw = act.ctrlrange?.[0] ?? -1;
              const ctrlMaxRaw = act.ctrlrange?.[1] ?? 1;
              const isVelocity = act.kind === "velocity" || act.kind === "intvelocity";
              const gearValue = Array.isArray(act.gear)
                ? act.gear[0]
                : typeof act.gear === "number"
                  ? act.gear
                  : 1;
              const updateGear = (v) => {
                if (!Number.isFinite(v)) return;
                if (Array.isArray(act.gear)) {
                  const next = [...act.gear];
                  next[0] = v;
                  patchActuator(act.uuid, { gear: next });
                } else {
                  patchActuator(act.uuid, { gear: v });
                }
              };

              return (
                <div
                  key={act.uuid}
                  ref={(el) => {
                    rowRefs.current[act.uuid] = el;
                  }}
                >
                  {actIdx > 0 ? <Separator /> : null}
                  <div
                    className={[
                      "pt-2 pb-1 space-y-3 rounded transition-colors duration-700",
                      highlightUuid === act.uuid ? "bg-sky-400/15" : "",
                    ].join(" ")}
                  >
                    <div className="flex items-center gap-2 min-w-0 pb-0.5 text-xs">
                      <Zap className="w-3 h-3 shrink-0 text-amber-500" aria-hidden />
                      <div className="flex min-w-0 flex-1 items-center gap-1">
                        {showEditControls && editingNameUuid === act.uuid ? (
                          <input
                            data-no-panel-drag="true"
                            type="text"
                            autoFocus
                            value={nameDraft}
                            aria-label="Actuator name"
                            onChange={(e) => setNameDraft(e.target.value)}
                            onMouseDown={(e) => e.stopPropagation()}
                            onBlur={() => commitNameEdit(act)}
                            onKeyDown={(e) => {
                              e.stopPropagation();
                              if (e.key === "Enter") {
                                e.preventDefault();
                                e.currentTarget.blur();
                              } else if (e.key === "Escape") {
                                e.preventDefault();
                                cancelNameEdit(act);
                              }
                            }}
                            className="min-w-0 flex-1 rounded border border-gray-600 bg-gray-800 px-2 py-1 text-xs font-medium text-gray-100 outline-none ring-amber-600/35 focus:border-amber-500/65 focus:ring-1"
                          />
                        ) : showEditControls ? (
                          <button
                            type="button"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={() => beginNameEdit(act)}
                            className="min-w-0 flex-1 truncate rounded px-0.5 py-0.5 text-left text-xs font-medium text-gray-200 outline-none hover:bg-gray-800/65 hover:text-white focus-visible:bg-gray-800/65 focus-visible:ring-1 focus-visible:ring-amber-600/55"
                          >
                            {act.name?.trim() ? act.name : "Actuator"}
                          </button>
                        ) : (
                          <span className="min-w-0 flex-1 truncate px-0.5 py-0.5 text-left text-xs font-medium text-gray-200">
                            {act.name?.trim() ? act.name : "Actuator"}
                          </span>
                        )}
                      </div>
                      {showEditControls && editingNameUuid !== act.uuid ? (
                        <>
                          <Tooltip content="Rename actuator">
                            <button
                              type="button"
                              aria-label="Rename actuator"
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={() => beginNameEdit(act)}
                              className={`${INSPECTOR_CHROME_ICON_BUTTON_CLASS} h-6 w-6 shrink-0`}
                            >
                              <Pencil className="h-3 w-3" aria-hidden />
                            </button>
                          </Tooltip>
                          <Tooltip
                            content={
                              disableAddActuator
                                ? "Stop physics to delete actuator"
                                : "Delete actuator"
                            }
                          >
                            <button
                              type="button"
                              disabled={disableAddActuator}
                              aria-label="Delete actuator"
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.preventDefault();
                                handleRemoveActuator(act.uuid);
                              }}
                              className={`${INSPECTOR_CHROME_ICON_DELETE_CLASS} h-6 w-6 shrink-0 disabled:!pointer-events-auto`}
                            >
                              <Trash2 className="h-3 w-3" aria-hidden />
                            </button>
                          </Tooltip>
                        </>
                      ) : null}
                    </div>

                    <ActuatorPhysicsPositionSlider actuator={act} jointOverride={actuatorJoint} />

                    {showEditControls ? (
                      <>
                        <div className="flex flex-row items-center gap-2 min-w-0 w-full">
                          <FieldLabel className="inline-flex items-center gap-1.5 shrink-0 whitespace-nowrap">
                            Kind
                            <InfoTooltip text={ACTUATOR_KIND_INFO_TEXT} widthClass="w-72" />
                          </FieldLabel>
                          <MenuSelect
                            value={act.kind ?? "motor"}
                            onChange={(v) => patchActuator(act.uuid, { kind: v })}
                            className="min-w-0 flex-1"
                            triggerClassName="min-w-0"
                            options={ACTUATOR_KINDS.map((k) => ({ value: k, label: k }))}
                            triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
                            aria-label="Actuator kind"
                          />
                        </div>

                        <div>
                          <FieldLabel className="block mb-1">Control Range</FieldLabel>
                          <div className="grid grid-cols-2 gap-2 w-full min-w-0 items-center">
                            {dragInput(
                              act.uuid,
                              "ctrlmin",
                              "Min",
                              ctrlMinRaw,
                              (v) => {
                                if (Number.isFinite(v))
                                  patchActuator(act.uuid, {
                                    ctrlrange: [v, ctrlMaxRaw],
                                  });
                              },
                              { step: "0.1", displayValueFormatter: formatCompactNumberInputValue }
                            )}
                            {dragInput(
                              act.uuid,
                              "ctrlmax",
                              "Max",
                              ctrlMaxRaw,
                              (v) => {
                                if (Number.isFinite(v))
                                  patchActuator(act.uuid, {
                                    ctrlrange: [ctrlMinRaw, v],
                                  });
                              },
                              { step: "0.1", displayValueFormatter: formatCompactNumberInputValue }
                            )}
                          </div>
                        </div>

                        {isPosition ? (
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              {dragInput(
                                act.uuid,
                                "kp",
                                "Position Gain (kp)",
                                act.kp ?? 50,
                                (v) => {
                                  if (Number.isFinite(v)) patchActuator(act.uuid, { kp: v });
                                },
                                {
                                  step: "1",
                                  tooltip:
                                    "Controls how strongly the actuator drives the joint toward its target position",
                                }
                              )}
                            </div>
                            <div>
                              {dragInput(
                                act.uuid,
                                "dampratio",
                                "Damp Ratio",
                                act.dampratio ?? 1,
                                (v) => {
                                  if (Number.isFinite(v)) patchActuator(act.uuid, { dampratio: v });
                                },
                                {
                                  step: "0.05",
                                  min: "0",
                                  tooltip:
                                    "Sets how much damping is applied to reduce overshoot and oscillation",
                                }
                              )}
                            </div>
                          </div>
                        ) : isVelocity ? (
                          <div className="grid grid-cols-2 gap-2 w-full min-w-0 items-start">
                            {dragInput(
                              act.uuid,
                              "kv",
                              "kv",
                              act.kv ?? 20,
                              (v) => {
                                if (Number.isFinite(v)) patchActuator(act.uuid, { kv: v });
                              },
                              { step: "1", min: "0", tooltip: "Velocity gain" }
                            )}
                            {dragInput(act.uuid, "gear", "Gear", gearValue, updateGear, {
                              step: "0.1",
                              tooltip: "Gear ratio / force scaling",
                            })}
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-2 w-full min-w-0 items-start">
                            {dragInput(
                              act.uuid,
                              "gain",
                              "Gain",
                              act.gainprm?.[0] ?? 1,
                              (v) => {
                                if (!Number.isFinite(v)) return;
                                const next = Array.isArray(act.gainprm)
                                  ? [...act.gainprm]
                                  : [1, 0, 0];
                                next[0] = v;
                                patchActuator(act.uuid, { gainprm: next });
                              },
                              { step: "0.1" }
                            )}
                            {dragInput(act.uuid, "gear", "Gear", gearValue, updateGear, {
                              step: "0.1",
                              tooltip: "Gear ratio / force scaling",
                            })}
                          </div>
                        )}

                        {isPosition && (
                          <div>
                            {dragInput(act.uuid, "gear", "Gear", gearValue, updateGear, {
                              step: "0.1",
                              tooltip: "Gear ratio / force scaling",
                            })}
                          </div>
                        )}
                      </>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </>
  );
};

export default React.memo(ActuatorAccordion);
